import { Injectable } from '@angular/core';
import type { DriveFile } from '@freelancer/datastore/collections';
import { isDefined } from '@freelancer/utils';

@Injectable({
  providedIn: 'root',
})
export class FileRead {
  /**
   * Type guard function that narrows an object to DriveFile or File
   */
  isDriveFile(file: DriveFile | File): file is DriveFile {
    return 'displayName' in file;
  }

  /**
   * Load a file into a base 64 string
   */
  async getBase64StringFromFile(fileToLoad: File): Promise<string> {
    return new Promise(async (resolve, reject) => {
      const reader = new FileReader();
      reader.onload = fileLoadedEvent => {
        if (
          isDefined(fileLoadedEvent.target) &&
          isDefined(fileLoadedEvent.target.result)
        ) {
          const { result } = fileLoadedEvent.target;
          if (result instanceof ArrayBuffer) {
            return reject(fileLoadedEvent);
          }
          return resolve(result);
        }
        return reject(fileLoadedEvent);
      };

      reader.onerror = error => {
        reader.abort();
        return reject(error);
      };

      reader.readAsDataURL(fileToLoad);
    });
  }

  /**
   * Transform encoded image data into a file object
   */
  readImageDataIntoFile(data: string, imageFileName?: string): File {
    const dataArray = data.split(',');
    const mimeString = dataArray[0].split(';')[0].split(':')[1];
    const decoded = atob(dataArray[1]);
    const byteArray = new Uint8Array(decoded.length);
    for (let i = 0; i < decoded.length; i++) {
      byteArray[i] = decoded.charCodeAt(i);
    }

    let filename = imageFileName;
    if (!filename) {
      // Assign current timestamp as default filename
      // e.g. 30-04-2019-055340.png
      const formattedDateString = new Date()
        .toLocaleString('en-GB')
        .replace(/[^ -~]/g, '')
        .replace(/[:,]/g, '')
        .replace(/[/ ]/g, '-');
      filename = `${formattedDateString}.${mimeString.split('/')[1]}`;
    }

    try {
      return new File([byteArray], filename, { type: mimeString });
    } catch (error: unknown) {
      /**
       * Browsers like Edge and IE don't support the File() constructor so we
       * resort to Blob for initialization
       */
      const blob = new Blob([byteArray], {
        type: mimeString,
      });
      const lastModifiedDate = new Date();
      return {
        ...blob,
        lastModified: lastModifiedDate.getTime(),
        name: filename,
        webkitRelativePath: '',
      };
    }
  }
}
